/**
 * QzPrintControls (Jun-2026, User-Wunsch) — dezenter Direkt-Druck-Block für
 * Etiketten/Aufkleber. Lädt das PDF von `pdfPath` (api), druckt per QZ Tray
 * ohne Dialog auf einen wählbaren Drucker. Browser-Druck bleibt als Fallback
 * über die bestehenden Buttons. Enthält einen dezenten Installations-Hinweis,
 * damit Nutzer QZ Tray selbst einrichten können.
 */
import { useState } from "react";
import { Printer, Loader2, Download, HelpCircle } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { Button } from "../ui/button";
import { connectQz, listPrinters, getDefaultPrinter, printPdfBase64, QZ_DOWNLOAD_URL } from "../../lib/qztray";

async function fetchPdfBase64(pdfPath) {
    const res = await api.get(pdfPath, { responseType: "blob" });
    const blob = new Blob([res.data], { type: "application/pdf" });
    return await new Promise((resolve, reject) => {
        const fr = new FileReader();
        fr.onload = () => resolve(String(fr.result).split(",")[1]);
        fr.onerror = reject;
        fr.readAsDataURL(blob);
    });
}

export default function QzPrintControls({ pdfPath, testid = "qz-print" }) {
    const [busy, setBusy] = useState(false);
    const [printers, setPrinters] = useState(null);
    const [selected, setSelected] = useState("");
    const [help, setHelp] = useState(false);

    const connect = async () => {
        setBusy(true);
        try {
            await connectQz();
            const [list, def] = await Promise.all([listPrinters(), getDefaultPrinter().catch(() => "")]);
            setPrinters(list);
            setSelected(def || list[0] || "");
            toast.success("QZ Tray verbunden.");
        } catch (e) {
            setHelp(true);
            toast.error("QZ Tray nicht erreichbar. Bitte installieren/starten.");
        } finally { setBusy(false); }
    };

    const print = async () => {
        if (!selected) { toast.error("Bitte Drucker wählen."); return; }
        setBusy(true);
        try {
            const b64 = await fetchPdfBase64(pdfPath);
            await printPdfBase64(selected, b64);
            toast.success(`Gedruckt auf „${selected}".`);
        } catch (e) {
            toast.error("Druck fehlgeschlagen. Ist QZ Tray gestartet?");
            setHelp(true);
        } finally { setBusy(false); }
    };

    return (
        <div className="w-full mt-3 pt-3 border-t border-gray-100" data-testid={`${testid}-block`}>
            <div className="flex items-center gap-2 flex-wrap">
                {printers === null ? (
                    <Button onClick={connect} disabled={busy} variant="outline" className="rounded-sm h-8 text-xs" data-testid={`${testid}-connect`}>
                        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Printer className="w-3.5 h-3.5 mr-1.5" />}
                        Direkt drucken (Etikettendrucker)
                    </Button>
                ) : (
                    <>
                        <select value={selected} onChange={(e) => setSelected(e.target.value)} className="border border-gray-300 rounded-sm px-2 py-1.5 text-xs bg-white max-w-[200px]" data-testid={`${testid}-select`}>
                            {printers.map((p) => <option key={p} value={p}>{p}</option>)}
                        </select>
                        <Button onClick={print} disabled={busy} className="rounded-sm h-8 text-xs bg-[#111827] hover:bg-black text-white" data-testid={`${testid}-print`}>
                            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Printer className="w-3.5 h-3.5 mr-1.5" />} Drucken
                        </Button>
                    </>
                )}
                <button onClick={() => setHelp((v) => !v)} className="text-[11px] text-gray-400 hover:text-gray-600 inline-flex items-center gap-1" data-testid={`${testid}-help-toggle`}>
                    <HelpCircle className="w-3 h-3" /> Drucker einrichten
                </button>
            </div>
            {help && (
                <div className="mt-2 text-[11px] text-gray-500 bg-gray-50 border border-gray-200 rounded-sm p-2.5 leading-relaxed" data-testid={`${testid}-help`}>
                    Für den Direktdruck auf einen festen Etikettendrucker (z. B. Zebra/Brother) installieren Sie einmalig{" "}
                    <a href={QZ_DOWNLOAD_URL} target="_blank" rel="noreferrer" className="text-indigo-600 font-bold underline inline-flex items-center gap-0.5" data-testid={`${testid}-download`}>
                        QZ Tray <Download className="w-3 h-3" />
                    </a>{" "}
                    (kostenlos). Nach der Installation läuft es im Hintergrund; beim ersten Druck einmal „Zulassen" bestätigen.
                    Ohne QZ Tray nutzen Sie weiterhin die „Aufkleber-PDF"-Schaltfläche (Browser-Druckdialog).
                </div>
            )}
        </div>
    );
}
